import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import PublicLayout from "@/components/public/PublicLayout";
import PortalLayout from "@/components/portal/PortalLayout";

import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import StoreLocator from "@/pages/StoreLocator";
import Franchise from "@/pages/Franchise";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Track from "@/pages/Track";
import Login from "@/pages/Login";

import AdminDashboard from "@/pages/portal/AdminDashboard";
import FranchisePipeline from "@/pages/portal/FranchisePipeline";
import ProductsAdmin from "@/pages/portal/ProductsAdmin";
import StoresAdmin from "@/pages/portal/StoresAdmin";
import OrdersAdmin from "@/pages/portal/OrdersAdmin";
import SuppliersAdmin from "@/pages/portal/SuppliersAdmin";
import FranchiseeDashboard from "@/pages/portal/FranchiseeDashboard";
import Inventory from "@/pages/portal/Inventory";
import PurchaseOrders from "@/pages/portal/PurchaseOrders";

const ADMIN = ["super_admin", "head_office"];
const FRANCHISE = ["franchise_owner", "store_manager"];

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/portal" replace />;
  return children;
}

function PortalIndex() {
  const { user } = useAuth();
  if (ADMIN.includes(user.role)) return <Navigate to="/portal/admin" replace />;
  if (FRANCHISE.includes(user.role)) return <Navigate to="/portal/store" replace />;
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/catalogue" element={<Catalogue />} />
                <Route path="/stores" element={<StoreLocator />} />
                <Route path="/franchise" element={<Franchise />} />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/track" element={<Track />} />
              </Route>

              <Route path="/login" element={<Login />} />

              <Route path="/portal" element={<Protected><PortalLayout /></Protected>}>
                <Route index element={<PortalIndex />} />
                <Route path="admin" element={<Protected roles={ADMIN}><AdminDashboard /></Protected>} />
                <Route path="pipeline" element={<Protected roles={ADMIN}><FranchisePipeline /></Protected>} />
                <Route path="products" element={<Protected roles={ADMIN}><ProductsAdmin /></Protected>} />
                <Route path="stores" element={<Protected roles={ADMIN}><StoresAdmin /></Protected>} />
                <Route path="orders" element={<Protected roles={ADMIN}><OrdersAdmin /></Protected>} />
                <Route path="suppliers" element={<Protected roles={ADMIN}><SuppliersAdmin /></Protected>} />
                <Route path="store" element={<Protected roles={FRANCHISE}><FranchiseeDashboard /></Protected>} />
                <Route path="inventory" element={<Protected roles={FRANCHISE}><Inventory /></Protected>} />
                <Route path="purchase-orders" element={<Protected roles={FRANCHISE}><PurchaseOrders /></Protected>} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
